import type { PropsWithChildren } from 'react';

import { Stack, Text } from '@mantine/core';

import { IconList } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../../../shared/components/UI/Button/Button';

import { useResponseProjectDetailContext } from '../../context/responseProjectDetailContext';
import { useResponseProjectGenerationStatus } from '../../hooks/useResponseProjectGenerationStatus.hooks';
import { Summary } from './components/summary/Summary';
import { AsideTab } from './components/tabs';

enum AsideTabValue {
  SUMMARY = 'summary',
}

interface ResponseProjectDetailAsideProps {
  projectId: number;
}

export function ResponseProjectDetailAside({
  projectId,
}: ResponseProjectDetailAsideProps) {
  const status = useResponseProjectGenerationStatus(projectId);

  if (status === 'pending') {
    return <GenerationLoading />;
  }

  return <Content />;
}

function GenerationLoading() {
  const { t } = useTranslation('bidResponse');
  return (
    <Wrapper>
      <Text size="sm" color="gray.5">
        {t('projectDetail.aside.loading.summary')}
      </Text>
    </Wrapper>
  );
}

function Content() {
  const { t } = useTranslation('bidResponse');
  const { onOpenCreateBlockModal, blocks } = useResponseProjectDetailContext();

  const handleCreateBlock = () => {
    onOpenCreateBlockModal({
      parentId: undefined,
      index: blocks.length + 1,
    });
  };
  return (
    <Wrapper>
      <Summary />
      <Button variant="light" onClick={handleCreateBlock}>
        {t('projectDetail.aside.addBlockCTA')}
      </Button>
    </Wrapper>
  );
}

function Wrapper({ children }: PropsWithChildren) {
  const { t } = useTranslation('bidResponse');
  const activeTab = AsideTabValue.SUMMARY;
  return (
    <Stack
      bg="white"
      w="100%"
      p="04"
      spacing="04"
      sx={theme => ({
        boxShadow: theme.shadows.md,
        borderRadius: 20,
      })}
    >
      <AsideTab
        icon={<IconList size={16} />}
        label={t('projectDetail.aside.tabs.summary')}
        value={AsideTabValue.SUMMARY}
        activeTab={activeTab}
      />
      {children}
    </Stack>
  );
}
